import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  MapPin, Phone, Mail, Clock, Star, Users, Eye, Package,
  Heart, MessageCircle, Share2, ChevronLeft, ExternalLink,
  Check, Loader2, Grid, Info, MessageSquare, Map
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { getDisplayNumber, getDisplayPlateNumber } from '../utils/digitMask';
import StoreBadges from '../components/StoreBadges';
import PriceOrContact from '../components/PriceOrContact';
import UnifiedStoreCard from '../components/marketplace/UnifiedStoreCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StorePage() {
  const { storeId } = useParams();
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);
  
  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  useEffect(() => {
    fetchStoreData();
    recordView();
  }, [storeId]);

  useEffect(() => {
    if (isLoggedIn) {
      checkFollowStatus();
    }
  }, [storeId, isLoggedIn]);

  const fetchStoreData = async () => {
    setLoading(true);
    try {
      // Fetch store details
      const storeRes = await axios.get(`${API_URL}/api/public/store/${storeId}`);
      if (storeRes.data.success) {
        setStore(storeRes.data.store);
      }
      
      // Fetch products
      const productsRes = await axios.get(`${API_URL}/api/public/store/${storeId}/products`);
      if (productsRes.data.success) {
        setProducts(productsRes.data.products);
      }
      
      // Fetch reviews
      const reviewsRes = await axios.get(`${API_URL}/api/public/store/${storeId}/reviews`);
      if (reviewsRes.data.success) {
        setReviews(reviewsRes.data.reviews);
      }
    } catch (error) {
      console.error('Error fetching store:', error);
      toast.error(isAr ? 'خطأ في تحميل بيانات المتجر' : 'Error loading store data');
    } finally {
      setLoading(false);
    }
  };

  const recordView = async () => {
    try {
      await axios.post(`${API_URL}/api/public/store/${storeId}/view`, {
        user_id: isLoggedIn ? JSON.parse(atob(token.split('.')[1])).sub : null
      });
    } catch (error) {
      // Silent fail for view recording
    }
  };

  const checkFollowStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/store/${storeId}/is-following`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setIsFollowing(res.data.following);
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const handleFollow = async () => {
    if (!isLoggedIn) {
      toast.error(isAr ? 'يرجى تسجيل الدخول للمتابعة' : 'Please login to follow');
      return;
    }
    
    setFollowLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/customer/store/${storeId}/follow`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setIsFollowing(res.data.following);
        // Update local store stats
        setStore(prev => ({
          ...prev,
          stats: {
            ...prev.stats,
            followers_count: prev.stats.followers_count + (res.data.following ? 1 : -1)
          }
        }));
        toast.success(res.data.following 
          ? (isAr ? 'تمت المتابعة!' : 'Following!') 
          : (isAr ? 'تم إلغاء المتابعة' : 'Unfollowed')
        );
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 401 || detail === 'Invalid authentication credentials') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error(isAr ? 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى' : 'Session expired. Please login again');
        window.location.href = '/login';
        return;
      }
      
      toast.error(isAr ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setFollowLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!isLoggedIn) {
      toast.error(isAr ? 'يرجى تسجيل الدخول لإضافة تقييم' : 'Please login to add a review');
      return;
    }
    
    if (!reviewText.trim()) {
      toast.error(isAr ? 'يرجى كتابة تعليق' : 'Please write a comment');
      return;
    }
    
    setSubmittingReview(true);
    try {
      const res = await axios.post(`${API_URL}/api/customer/store/${storeId}/review`, {
        rating: reviewRating,
        comment: reviewText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setReviews(prev => [res.data.review, ...prev]);
        setReviewText('');
        setReviewRating(5);
        toast.success(isAr ? 'تم إضافة التقييم!' : 'Review added!');
        // Update store rating
        fetchStoreData();
      }
    } catch (error) {
      console.error('Review error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      if (status === 401 || detail === 'Invalid authentication credentials') {
        // Token expired or invalid - clear and redirect
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error(isAr ? 'انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى' : 'Session expired. Please login again');
        window.location.href = '/login';
        return;
      }
      
      if (detail === 'You have already reviewed this store') {
        toast.error(isAr ? 'لقد قمت بتقييم هذا المتجر مسبقاً' : 'You have already reviewed this store');
        return;
      }
      
      const msg = detail || (isAr ? 'حدث خطأ' : 'An error occurred');
      toast.error(msg);
    } finally {
      setSubmittingReview(false);
    }
  };

  const getStoreTypeLabel = (type) => {
    const types = {
      phone_numbers: isAr ? 'متجر أرقام موبايل' : 'Mobile Numbers Store',
      car_plates: isAr ? 'متجر لوحات سيارات' : 'Car Plates Store',
      auto: isAr ? 'متجر سيارات' : 'Auto Store',
      real_estate: isAr ? 'متجر عقارات' : 'Real Estate Store',
      numbers: isAr ? 'أرقام مميزة' : 'VIP Numbers',
      cars: isAr ? 'سيارات' : 'Cars',
      products: isAr ? 'منتجات' : 'Products',
      general: isAr ? 'متجر عام' : 'General Store'
    };
    return types[type] || types.general;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ar-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`}>
        <Loader2 className="h-12 w-12 animate-spin text-[#1E9BD9]" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`}>
        <Package className={`h-20 w-20 mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          {isAr ? 'المتجر غير موجود' : 'Store not found'}
        </h2>
        <Link to="/" className="text-[#1E9BD9] hover:underline">
          {isAr ? 'العودة للرئيسية' : 'Back to home'}
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'products', label: isAr ? 'المنتجات' : 'Products', icon: Grid },
    { id: 'reviews', label: isAr ? 'الريفيو' : 'Reviews', icon: MessageSquare },
    { id: 'about', label: isAr ? 'تفاصيل' : 'Details', icon: Info },
  ];

  // Split products into offers (with discount) and regular
  const offersProducts = products.filter(p => p.hasDiscount || (p.salePrice && p.price && p.salePrice < p.price));
  const regularProducts = products.filter(p => !p.hasDiscount && !(p.salePrice && p.price && p.salePrice < p.price));

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`}>
      {/* Banner */}
      <div className="relative h-48 md:h-64 lg:h-80">
        <img 
          src={store.banner || 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&h=400&fit=crop'}
          alt={store.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        
        {/* Back button */}
        <Link 
          to="/"
          className="absolute top-4 left-4 bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/30 transition-colors"
        >
          <ChevronLeft className="h-6 w-6 text-white" />
        </Link>
        
        {/* Share button */}
        <button 
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            toast.success(isAr ? 'تم نسخ الرابط!' : 'Link copied!');
          }}
          className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/30 transition-colors"
        >
          <Share2 className="h-6 w-6 text-white" />
        </button>
      </div>

      {/* Store Info Section */}
      <div className="max-w-6xl mx-auto px-4 -mt-20 relative z-10">
        <div className={`rounded-2xl shadow-lg p-6 ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'}`}>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Logo */}
            <div className="flex-shrink-0">
              <img 
                src={store.logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(store.name)}&size=120&background=1E9BD9&color=fff`}
                alt={store.name}
                className="w-28 h-28 md:w-32 md:h-32 rounded-2xl object-cover border-4 border-white shadow-lg"
              />
            </div>
            
            {/* Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {isAr ? store.name : (store.name_en || store.name)}
                    </h1>
                    {store.verified && (
                      <span className="bg-[#1E9BD9] text-white p-1 rounded-full">
                        <Check className="h-4 w-4" />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm ${
                      isDarkMode ? 'bg-white/10 text-gray-300' : 'bg-[#F1F5F9] text-[#64748B]'
                    }`}>
                      {getStoreTypeLabel(store.type)}
                    </span>
                    <StoreBadges owner={store.owner} size="md" />
                  </div>
                  <p className={`mt-2 max-w-2xl ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                    {isAr ? store.description : (store.description_en || store.description)}
                  </p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={isFollowing 
                      ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' 
                      : 'bg-[#1E9BD9] hover:bg-[#1789C2] text-white'
                    }
                  >
                    {followLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isFollowing ? (
                      <><Check className="h-4 w-4 me-2" /> {isAr ? 'متابَع' : 'Following'}</>
                    ) : (
                      <><Heart className="h-4 w-4 me-2" /> {isAr ? 'متابعة' : 'Follow'}</>
                    )}
                  </Button>
                  
                  {store.contact?.whatsapp && (
                    <a
                      href={`https://wa.me/${store.contact.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#25D366] hover:bg-[#20BD5A] text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <MessageCircle className="h-4 w-4" />
                      {isAr ? 'واتساب' : 'WhatsApp'}
                    </a>
                  )}
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-gray-200 dark:border-white/10">
                <div className="text-center">
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {store.stats?.followers_count || 0}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                    <Users className="h-4 w-4 inline me-1" />
                    {isAr ? 'متابع' : 'Followers'}
                  </p>
                </div>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {store.stats?.views_count || 0}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                    <Eye className="h-4 w-4 inline me-1" />
                    {isAr ? 'مشاهدة' : 'Views'}
                  </p>
                </div>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {store.stats?.products_count || products.length}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                    <Package className="h-4 w-4 inline me-1" />
                    {isAr ? 'منتج' : 'Products'}
                  </p>
                </div>
                <div className="text-center">
                  <p className={`text-2xl font-bold text-yellow-500`}>
                    {store.stats?.rating?.toFixed(1) || '0.0'}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                    <Star className="h-4 w-4 inline me-1 text-yellow-500" />
                    ({store.stats?.reviews_count || 0})
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className={`mt-6 rounded-2xl shadow-lg ${isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white'}`}>
          {/* Tab Headers */}
          <div className="flex border-b border-gray-200 dark:border-white/10">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 px-6 text-center font-medium transition-colors flex items-center justify-center gap-2 ${
                  activeTab === tab.id
                    ? 'text-[#1E9BD9] border-b-2 border-[#1E9BD9]'
                    : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-[#64748B] hover:text-[#0F172A]'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Products Tab */}
            {activeTab === 'products' && (
              <div className="space-y-8">
                {/* Offers Section */}
                {offersProducts.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-4 py-1.5 rounded-full text-sm font-bold">
                        {isAr ? 'العروض' : 'Offers'}
                      </div>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {offersProducts.length} {isAr ? 'منتج' : 'items'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                      {offersProducts.map((product, index) => (
                        <div key={product.id || index} className="relative">
                          {(() => {
                            const discountPercent = product.discountPercentage || (product.price && product.salePrice ? Math.round((product.price - product.salePrice) / product.price * 100) : 0);
                            return discountPercent > 0 ? (
                              <div className="absolute top-2 left-2 z-10 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] sm:text-xs font-bold px-2 py-1 rounded-full pointer-events-none">
                                -{discountPercent}%
                              </div>
                            ) : null;
                          })()}
                          <UnifiedStoreCard product={product} store={store} isDarkMode={isDarkMode} isAr={isAr} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Regular Products Section */}
                {regularProducts.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`px-4 py-1.5 rounded-full text-sm font-bold ${isDarkMode ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>
                        {isAr ? 'المنتجات' : 'Products'}
                      </div>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {regularProducts.length} {isAr ? 'منتج' : 'items'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                      {regularProducts.map((product, index) => (
                        <UnifiedStoreCard
                          key={product.id || index}
                          product={product}
                          store={store}
                          isDarkMode={isDarkMode}
                          isAr={isAr}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* No Products */}
                {products.length === 0 && (
                  <div className="text-center py-12">
                    <Package className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}>
                      {isAr ? 'لا توجد منتجات حالياً' : 'No products available'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Details Tab (تفاصيل) */}
            {activeTab === 'about' && (
              <div className="space-y-6">
                {/* Store Description */}
                <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
                  <h3 className={`font-bold text-lg mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    <Info className="h-5 w-5 text-[#1E9BD9]" />
                    {isAr ? 'نبذة عن المتجر' : 'About Store'}
                  </h3>
                  <p className={`leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                    {isAr ? store.description : (store.description_en || store.description) || (isAr ? 'لا يوجد وصف متاح' : 'No description available')}
                  </p>
                </div>

                {/* Store Type */}
                <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
                  <h3 className={`font-bold text-lg mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    <Package className="h-5 w-5 text-[#1E9BD9]" />
                    {isAr ? 'نوع المتجر' : 'Store Type'}
                  </h3>
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                    isDarkMode ? 'bg-[#1E9BD9]/20 text-[#1E9BD9]' : 'bg-[#1E9BD9]/10 text-[#1E9BD9]'
                  }`}>
                    {getStoreTypeLabel(store.type)}
                  </span>
                </div>

                {/* Contact Info */}
                {store.contact && (
                  <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
                    <h3 className={`font-bold text-lg mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      <Phone className="h-5 w-5 text-[#1E9BD9]" />
                      {isAr ? 'معلومات التواصل' : 'Contact Info'}
                    </h3>
                    <div className="space-y-3">
                      {store.contact.phone && (
                        <a href={`tel:${store.contact.phone}`} className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-white hover:bg-gray-50 text-[#64748B]'}`}>
                          <Phone className="h-5 w-5 text-[#1E9BD9]" />
                          <span dir="ltr" className="font-medium">{store.contact.phone}</span>
                        </a>
                      )}
                      {store.contact.whatsapp && (
                        <a 
                          href={`https://wa.me/${store.contact.whatsapp.replace(/[^0-9]/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isDarkMode ? 'bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366]' : 'bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366]'}`}
                        >
                          <MessageCircle className="h-5 w-5" />
                          <span dir="ltr" className="font-medium">{store.contact.whatsapp}</span>
                        </a>
                      )}
                      {store.contact.email && (
                        <a href={`mailto:${store.contact.email}`} className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isDarkMode ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-white hover:bg-gray-50 text-[#64748B]'}`}>
                          <Mail className="h-5 w-5 text-[#1E9BD9]" />
                          <span className="font-medium">{store.contact.email}</span>
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Location */}
                {store.location && (
                  <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
                    <h3 className={`font-bold text-lg mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      <MapPin className="h-5 w-5 text-[#1E9BD9]" />
                      {isAr ? 'الموقع' : 'Location'}
                    </h3>
                    <p className={isDarkMode ? 'text-gray-300' : 'text-[#64748B]'}>
                      {store.location.address || `${store.location.area}, ${store.location.emirate}`}
                    </p>
                  </div>
                )}

                {/* Working Hours */}
                {store.working_hours && (
                  <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
                    <h3 className={`font-bold text-lg mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      <Clock className="h-5 w-5 text-[#1E9BD9]" />
                      {isAr ? 'ساعات العمل' : 'Working Hours'}
                    </h3>
                    <p className={isDarkMode ? 'text-gray-300' : 'text-[#64748B]'}>
                      {store.working_hours.sunday?.open} - {store.working_hours.sunday?.close}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Reviews Tab (الريفيو) */}
            {activeTab === 'reviews' && (
              <div className="space-y-6">
                {/* Rating Summary */}
                <div className={`p-5 rounded-xl text-center ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-4xl font-bold text-yellow-500">
                      {store.stats?.rating?.toFixed(1) || '0.0'}
                    </span>
                    <Star className="h-8 w-8 text-yellow-500 fill-yellow-500" />
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {isAr ? `بناءً على ${store.stats?.reviews_count || 0} تقييم` : `Based on ${store.stats?.reviews_count || 0} reviews`}
                  </p>
                </div>

                {/* Add Review Form */}
                {isLoggedIn ? (
                  <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
                    <h3 className={`font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      <MessageSquare className="h-5 w-5 text-[#1E9BD9]" />
                      {isAr ? 'أضف تقييمك' : 'Add your review'}
                    </h3>
                    <div className="flex gap-2 mb-4">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button 
                          key={star}
                          onClick={() => setReviewRating(star)}
                          className="focus:outline-none transition-transform hover:scale-110"
                        >
                          <Star 
                            className={`h-8 w-8 ${star <= reviewRating ? 'text-yellow-500 fill-yellow-500' : isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} 
                          />
                        </button>
                      ))}
                    </div>
                    <Textarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder={isAr ? 'اكتب تعليقك هنا...' : 'Write your comment here...'}
                      className={`mb-4 ${isDarkMode ? 'bg-white/10 border-white/20 text-white placeholder:text-gray-500' : ''}`}
                      rows={3}
                    />
                    <Button
                      onClick={handleSubmitReview}
                      disabled={submittingReview || !reviewText.trim()}
                      className="bg-[#1E9BD9] hover:bg-[#1789C2] w-full md:w-auto"
                    >
                      {submittingReview ? (
                        <Loader2 className="h-4 w-4 animate-spin me-2" />
                      ) : (
                        <Star className="h-4 w-4 me-2" />
                      )}
                      {isAr ? 'إرسال التقييم' : 'Submit Review'}
                    </Button>
                  </div>
                ) : (
                  <div className={`p-5 rounded-xl text-center ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
                    <p className={`mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {isAr ? 'سجل دخول لإضافة تقييمك' : 'Login to add your review'}
                    </p>
                    <Link to="/login" className="text-[#1E9BD9] hover:underline font-medium">
                      {isAr ? 'تسجيل الدخول' : 'Login'}
                    </Link>
                  </div>
                )}

                {/* Reviews List */}
                {reviews.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                    <p className={isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}>
                      {isAr ? 'لا توجد تقييمات بعد. كن أول من يقيّم!' : 'No reviews yet. Be the first to review!'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div 
                        key={review.id}
                        className={`p-5 rounded-xl ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${isDarkMode ? 'bg-[#1E9BD9]' : 'bg-[#1E9BD9]'}`}>
                              {review.user_name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div>
                              <span className={`font-medium block ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                                {review.user_name}
                              </span>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
                                {new Date(review.created_at).toLocaleDateString('ar-AE', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star 
                                key={star}
                                className={`h-4 w-4 ${star <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
                              />
                            ))}
                          </div>
                        </div>
                        <p className={`leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                          {review.comment}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Bottom padding */}
      <div className="h-20" />
    </div>
  );
}
